/**
 * @fileoverview Syndicator Showdown — Invite Friends Dashboard Card
 * @module components/share/ShareInviteCard
 *
 * Generates (or reuses) the player's referral code and offers Copy Link /
 * Share / QR Code actions. Decorative artwork sits behind the text via CSS
 * background-image with a gradient fallback so the card looks intentional
 * even before the final artwork asset exists (see ShareInviteCard.css).
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { getOrCreateReferralCode } from '../../services/referralService';
import { buildInviteUrl, copyToClipboard, canNativeShare, nativeShare } from '../../utils/shareLinks';
import ShareQrModal from './ShareQrModal';
import './ShareInviteCard.css';

export default function ShareInviteCard({ user, userDoc }) {
  const [referralCode, setReferralCode] = useState(userDoc?.referralCode || null);
  const [stats, setStats]               = useState(null); // { clicks, signups }
  const [copied, setCopied]             = useState(false);
  const [qrOpen, setQrOpen]             = useState(false);
  const [error, setError]               = useState(false);
  // Guards against firing a second getOrCreateReferralCode() call while the
  // first is still in flight (e.g. the userDoc snapshot re-fires before the
  // generated code has been written back to users/{uid}).
  const generatingRef = useRef(false);

  // Generate/fetch the referral code once we know who the user is.
  useEffect(() => {
    if (!user) return;
    if (userDoc?.referralCode) { setReferralCode(userDoc.referralCode); return; }
    if (generatingRef.current) return;

    let cancelled = false;
    generatingRef.current = true;
    getOrCreateReferralCode(user, userDoc)
      .then((code) => { if (!cancelled) setReferralCode(code); })
      .catch((err) => {
        console.error('[ShareInviteCard] getOrCreateReferralCode error:', err);
        if (!cancelled) setError(true);
      })
      .finally(() => { generatingRef.current = false; });
    return () => { cancelled = true; };
  }, [user, userDoc]);

  // Live-subscribe to the referral doc for the optional "invited/joined" stats.
  useEffect(() => {
    if (!referralCode) return undefined;
    return onSnapshot(
      doc(db, 'user_referrals', referralCode),
      (snap) => { if (snap.exists()) setStats(snap.data()); },
      () => {} // stats are a nice-to-have — never surface this as an error
    );
  }, [referralCode]);

  const inviteUrl = referralCode ? buildInviteUrl(referralCode) : null;

  const handleCopy = useCallback(async () => {
    if (!inviteUrl) return;
    const ok = await copyToClipboard(inviteUrl);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  }, [inviteUrl]);

  const handleShare = useCallback(async () => {
    if (!inviteUrl) return;
    const shared = await nativeShare({
      title: 'Join me on Syndicator Showdown',
      text:  'Build your racing stable and take me on in Syndicator Showdown.',
      url:   inviteUrl,
    });
    if (!shared) handleCopy();
  }, [inviteUrl, handleCopy]);

  if (error) return null; // Fail quiet — the invite card is a nice-to-have, not core gameplay

  return (
    <div className="share-card share-card--invite">
      <div className="share-card__overlay" />
      <div className="share-card__content">
        <span className="share-card__eyebrow">🐎 CHALLENGE YOUR FRIENDS</span>
        <h3 className="share-card__title">Build your stable. Invite a mate. See who wins.</h3>
        <p className="share-card__subtitle">
          Share your stable, invite a mate, and see who builds the stronger racing empire.
        </p>

        {stats && (stats.clicks > 0 || stats.signups > 0) && (
          <div className="share-card__stats">
            <div className="share-card__stat">
              <span className="share-card__stat-val">{stats.clicks || 0}</span>
              <span className="share-card__stat-label">Friends Invited</span>
            </div>
            <div className="share-card__stat-divider" />
            <div className="share-card__stat">
              <span className="share-card__stat-val">{stats.signups || 0}</span>
              <span className="share-card__stat-label">Friends Joined</span>
            </div>
          </div>
        )}

        <div className="share-card__actions">
          <button
            type="button"
            className="share-card__btn share-card__btn--primary"
            onClick={handleCopy}
            disabled={!inviteUrl}
          >
            {copied ? '✓ Link Copied' : 'Copy Invite Link'}
          </button>
          {canNativeShare() && (
            <button
              type="button"
              className="share-card__btn share-card__btn--secondary"
              onClick={handleShare}
              disabled={!inviteUrl}
            >
              Share Link
            </button>
          )}
          <button
            type="button"
            className="share-card__btn share-card__btn--secondary"
            onClick={() => setQrOpen(true)}
            disabled={!inviteUrl}
          >
            Show QR Code
          </button>
        </div>
      </div>

      {inviteUrl && (
        <ShareQrModal
          isOpen={qrOpen}
          onClose={() => setQrOpen(false)}
          url={inviteUrl}
          title="Invite Friends"
          description="Share this link or QR code so a friend can join Syndicator Showdown and build their own stable."
          shareTitle="Join me on Syndicator Showdown"
          shareText="Build your racing stable and take me on in Syndicator Showdown."
          qrLabel="Scan to join Syndicator Showdown"
        />
      )}
    </div>
  );
}
